import { useDispatch, useSelector } from 'react-redux';
import { PatchTransaction, TransactionDetails } from 'store/transactions/types';
import { useCallback, useEffect, useMemo } from 'react';
import {
  selectPastTransactions,
  selectPatchTransactionsStatus,
  selectTransaction,
  selectTransactions,
  selectTypeOfPatchTransaction,
} from 'store/transactions/selectors';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';
import { useMiniappSdk } from 'components/miniapp-sdk-provider/miniapp-sdk.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { checkIn, setCurrentTransaction } from '../../../store/transactions/actions';

interface UseReceiptReturnValues {
  transaction: TransactionDetails;
  onCancelBookingClick: VoidFunction;
  patchTransactionData: Pick<PatchTransaction, 'status' | 'type'>;
  isSwipeModalContent: boolean;
  onCheckInClick: VoidFunction;
  isDeepLinkAccess: boolean;
}

interface UseReceiptProps {
  transactionId?: number | string;
  setIsCancelBookingStep: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useReceipt = ({ transactionId, setIsCancelBookingStep }: UseReceiptProps): UseReceiptReturnValues => {
  const patchTransactionStatus = useSelector(selectPatchTransactionsStatus);
  const typeOfPatchTransaction = useSelector(selectTypeOfPatchTransaction);
  const patchTransactionData = {
    status: patchTransactionStatus,
    type: typeOfPatchTransaction,
  };
  const transactions = useSelector(selectTransactions);
  const pastTransactions = useSelector(selectPastTransactions);
  const { showMiniappPaymentsNavigation, enable2WayChatRedesignResourceBooking } = useFlags();
  const isMobileDevice = useIsSmallViewportWidth();
  const client = useMiniappSdk();
  const selectedTransaction = useSelector(selectTransaction);
  const params = useSearchParams();

  // RES-999: de-modal booking details — kill the SwipeModal presentation everywhere Receipt is
  // rendered (including the receipt/:transactionUuid deep-link entry point, not just the
  // my-bookings click flow that now routes to BookingDetailsPage instead).
  const isSwipeModalContent: boolean =
    showMiniappPaymentsNavigation && isMobileDevice && !enable2WayChatRedesignResourceBooking;

  /*
   * Both lists, and the shared slot only when it holds the booking asked for. `transactions` is
   * `is_upcoming=true`, so a past booking is not in it. The slot is app-wide -- checkout, the
   * redirect and the restore all write to it -- so an unguarded fallback hands back someone else's
   * booking; it is kept because it is the only answer for a cold deeplink to a past booking.
   */
  const findTargetTransaction = useCallback((): TransactionDetails | undefined => {
    if (transactionId !== undefined && transactionId !== null && transactionId !== '') {
      // Compare as strings — details.id is typed `string | number` and the id can arrive here
      // having round-tripped through a URL param (always a string, see BookingDetailsPage), so a
      // strict === would silently miss whenever the two sides don't share the same JS type even
      // though they represent the same id (e.g. 42 !== "42").
      const matchesId = ({ details }: TransactionDetails): boolean => String(details?.id) === String(transactionId);

      return (
        transactions?.find(matchesId) ??
        pastTransactions?.find(matchesId) ??
        (selectedTransaction && matchesId(selectedTransaction) ? selectedTransaction : undefined)
      );
    }

    if (params?.transactionUuid) {
      const matchesUuid = ({ uuid }: TransactionDetails): boolean => uuid === params.transactionUuid;

      return (
        transactions?.find(matchesUuid) ??
        pastTransactions?.find(matchesUuid) ??
        (selectedTransaction && matchesUuid(selectedTransaction) ? selectedTransaction : undefined)
      );
    }

    /* No identifier at all: the slot IS the question, so there is nothing here to guard against. */
    return transactions?.find(({ id }) => id === selectedTransaction?.id) ?? selectedTransaction;
  }, [transactions, pastTransactions, transactionId, selectedTransaction, params?.transactionUuid]);

  const transaction = useMemo<TransactionDetails | undefined>(() => {
    return findTargetTransaction();
  }, [findTargetTransaction]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isSwipeModalContent) {
      client?.header.hideHeader();
      client?.swipe.disableBackSwipe();
    }

    return () => {
      client?.header.showHeader();
      client?.swipe.enableBackSwipe();
    };
  }, [client, isSwipeModalContent]);

  useEffect(() => {
    if (transaction) {
      dispatch(setCurrentTransaction(transaction));
    }
  }, [transaction, dispatch]);

  const onCancelBookingClick = useCallback(() => {
    setIsCancelBookingStep(true);
  }, [setIsCancelBookingStep]);

  const onCheckInClick = useCallback(() => {
    dispatch(checkIn.request(transaction?.uuid));
  }, [dispatch, transaction?.uuid]);

  return {
    isSwipeModalContent,
    onCancelBookingClick,
    patchTransactionData,
    transaction,
    onCheckInClick,
    isDeepLinkAccess: !!params?.transactionUuid,
  };
};
